import React from "react";
import editIcon from "@/assets/icons/edit-light.svg";
import trashIcon from "@/assets/icons/trash.svg";
import plusIcon from "@/assets/icons/plus.svg";
import styles from "./AudioCard.module.scss";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { truncateDescription } from "@/helpers/truncateDescr";
import { FormControlLabel, Skeleton, Switch } from "@mui/material";
import crossIcon from "@/assets/icons/cross.svg";
import { IMedia, MediaAccessType } from "@/store/models/video.models";
import {
  useRegenerateThumbnailMutation,
  useUpdateMediaMutation,
} from "@/store/rtk-api/media.api";
import { Button, notification } from "antd";

type Props = {
  audio: IMedia;
  handleToggleEditModal?: (music: IMedia) => void;
  handleToggleDeleteModal?: (music: IMedia) => void;
  handleTogglePublishModal?: (video: IMedia) => void;
  handleToggleDeleteAudioModal?: (audio: IMedia) => void;
  isEdit: boolean;
  isFeatured?: boolean;
  isLoading?: boolean;
};

export const AudioCard = ({
  audio,
  handleToggleDeleteModal,
  handleToggleEditModal,
  handleTogglePublishModal,
  handleToggleDeleteAudioModal,
  isEdit,
  isFeatured,
  isLoading,
}: Props) => {
  const [updateMedia] = useUpdateMediaMutation();
  const [regenerateThumbnail, { isLoading: isLoagingRegenerate }] =
    useRegenerateThumbnailMutation();

  const handleUpdateIsRecommended = async () => {
    try {
      const updatedSections = audio.sections.includes("Recommendation")
        ? audio.sections.filter((section) => section !== "Recommendation")
        : [...audio.sections, "Recommendation"];
      await updateMedia({
        uid: audio.uid,
        sections: updatedSections,
      }).unwrap();
    } catch (error) {
      console.error("Failed to update the video:", error);
    }
  };

  const handleChangePremium = async () => {
    try {
      const newAccessType =
        audio.accessType === MediaAccessType.BASE
          ? MediaAccessType.STORYTELLER_BASIC
          : MediaAccessType.BASE;

      await updateMedia({
        uid: audio.uid,
        accessType: newAccessType,
      }).unwrap();
    } catch (error) {
      console.error("Failed to update the music:", error);
    }
  };

  const handleRegenerateThumbnail = async () => {
    try {
      await regenerateThumbnail({ mediaUid: audio.uid }).unwrap();
    } catch (error: any) {
      console.error("Failed to regenerate thumbnail:", error);
      notification.error({
        message: "Failed to regenerate thumbnail",
        description: error,
      });
    }
  };

  return (
    <div className={styles.item} key={audio.uid}>
      <div className={styles.itemHeader}>
        <h5 className={styles.itemTitle}>{audio.title}</h5>
        <div className={styles.itemHeaderBtns}>
          {isLoading ? (
            <>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
            </>
          ) : (
            <>
              <div className={styles.itemHeaderLikes}>
                <FavoriteBorderOutlinedIcon />
                <span>{audio.likes}</span>
              </div>
              <div className={styles.itemHeaderLikes}>
                <VisibilityOutlinedIcon />
                <span>{audio.viewsCount}</span>
              </div>
              {isEdit && handleToggleEditModal && (
                <button
                  className={styles.itemHeaderEditBtn}
                  onClick={() => handleToggleEditModal(audio)}
                >
                  <img
                    className={styles.itemHeaderBtnImg}
                    src={editIcon}
                    alt="edit"
                  />
                </button>
              )}
              {handleToggleDeleteAudioModal && (
                <button
                  className={styles.itemHeaderAddBtn}
                  onClick={() => handleToggleDeleteAudioModal(audio)}
                >
                  <img
                    className={styles.itemHeaderBtnImg}
                    src={
                      audio.sections.includes("Recommendation")
                        ? crossIcon
                        : plusIcon
                    }
                    alt="add item"
                  />
                </button>
              )}
              {isFeatured && (
                <button
                  className={styles.itemHeaderAddBtn}
                  onClick={handleUpdateIsRecommended}
                >
                  <img
                    className={styles.itemHeaderBtnImg}
                    src={
                      audio.sections.includes("Recommendation")
                        ? crossIcon
                        : plusIcon
                    }
                    alt={`${audio.sections.includes("Recommendation") ? "remove item" : "add item"}`}
                  />
                </button>
              )}
              {handleToggleDeleteModal && (
                <button
                  className={styles.itemHeaderDeleteBtn}
                  onClick={() => handleToggleDeleteModal(audio)}
                >
                  <img
                    className={styles.itemHeaderBtnImg}
                    src={trashIcon}
                    alt="delete"
                  />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {!isLoading && (
        <div className={styles.premium}>
          <FormControlLabel
            label="Premium"
            control={
              <Switch
                checked={audio.accessType !== MediaAccessType.BASE}
                onChange={handleChangePremium}
                sx={{
                  "& .css-byenzh-MuiButtonBase-root-MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track":
                    {
                      backgroundColor:
                        audio.accessType !== MediaAccessType.BASE
                          ? "#008000"
                          : "#3f51b5",
                    },
                  "& .MuiSwitch-thumb": {
                    backgroundColor:
                      audio.accessType !== MediaAccessType.BASE
                        ? "#008000"
                        : "#3f51b5",
                    boxShadow: "#FF000066",
                  },
                }}
              />
            }
          />

          {handleTogglePublishModal && (
            <FormControlLabel
              control={
                <Switch
                  checked={audio.isPublished}
                  onChange={() => handleTogglePublishModal(audio)}
                  sx={{
                    "& .css-byenzh-MuiButtonBase-root-MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track":
                      {
                        backgroundColor: "#3f51b5",
                      },
                    "& .MuiSwitch-thumb": {
                      backgroundColor: "#3f51b5",
                      boxShadow: "#FF000066",
                    },
                  }}
                />
              }
              label="Published"
            />
          )}

          <Button
            className={styles.regenerate}
            onClick={handleRegenerateThumbnail}
            loading={isLoagingRegenerate}
          >
            Regenerate thumbnail
          </Button>
        </div>
      )}

      <div className={styles.itemDescr}>
        {" "}
        {truncateDescription(audio.description)}
      </div>

      {!isLoading ? (
        <img
          src={audio.thumbnailUrl as string}
          alt={audio.thumbnailUrl}
          className={styles.itemImg}
        />
      ) : (
        <div className={styles.loaderContainer}>
          <Skeleton variant="rounded" width="100%" height="300px" />
        </div>
      )}
    </div>
  );
};
